import { useEffect, useState } from 'react'
import { App } from '@capacitor/app'
import { cachedDeviceClock, hasDeviceClock, readDeviceClock } from '../lib/deviceClock'
import type { HourCycle } from '../lib/timeFormat'

/**
 * The phone's 12/24-hour setting, or null on the web and older shells.
 * Re-read whenever the app returns to the foreground, since that's when the
 * user may have just flipped it in the phone's settings. `appStateChange`, not
 * `visibilitychange`: Android's WebView never fires the latter on resume.
 */
export function useDeviceClock(): HourCycle | null {
  const [cycle, setCycle] = useState<HourCycle | null>(() => (hasDeviceClock() ? cachedDeviceClock() : null))

  useEffect(() => {
    if (!hasDeviceClock()) return
    let disposed = false
    let resumeSub: { remove: () => void } | undefined
    const refresh = () => {
      void readDeviceClock().then(next => {
        if (!disposed && next) setCycle(next)
      })
    }
    refresh()
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refresh()
    }).then(h => {
      if (disposed) h.remove()
      else resumeSub = h
    })
    return () => {
      disposed = true
      resumeSub?.remove()
    }
  }, [])

  return cycle
}
